// import { isPrefersReducedMotion } from '../../../utils/prefers-reduced-motion'
import { throttle } from '../../../utils/throttle'

/**
 * Carousel
 */
export class GMXCarousel extends HTMLElement {
	slidesCollectionEl: HTMLElement
	controlsContainerEl: HTMLElement
	slideEls: NodeListOf<HTMLElement>
	rtlModifier: number

	private resizeObserver: ResizeObserver

	static DRAG_THRESHOLD = 15

	private boundControlsClickHandler = this._handleControlsClick.bind(this)
	private boundDragStartHandler = throttle(this._handleDragStart.bind(this), 250)
	private boundDragMoveHandler = this._handleDragMove.bind(this)
	private boundDragEndHandler = this._handleDragEnd.bind(this)
	private boundMouseLeaveHandler = this._handleMouseLeave.bind(this)
	private boundClickHandler = this._handleClick.bind(this)

	// Drag/swipe variables
	private isGrabbed = false
	private grabbedX: number = null
	private xDiff = 0 // Offset for how much the cursor has moved from original position
	private lastSlide: HTMLElement
	private slidesInTransition = false
	private prevTime = Date.now()
	private mouseVelocity = 0
	private movedBeyondThreshold = false

	private actSlideNum = 0
	private get activeSlideNum(): number {
		return this.actSlideNum
	}
	private set activeSlideNum(val: number) {
		this.actSlideNum = val
	}

	get loop(): boolean {
		return this.hasAttribute('data-loop') && this.getAttribute('data-loop') === 'true'
	}
	set loop(val: boolean) {
		this.setAttribute('data-loop', val ? 'true' : 'false')
	}

	get offsetWithPeek(): boolean {
		return this.hasAttribute('data-offset-w-peek') && this.getAttribute('data-offset-w-peek') === 'true'
	}
	set offsetWithPeek(val: boolean) {
		this.setAttribute('data-offset-w-peek', val ? 'true' : 'false')
	}

	get transitionOverride(): string {
		return this.getAttribute('data-transition-override')
	}
	set transitionOverride(val: string) {
		this.setAttribute('data-transition-override', val)
	}

	get disableDragging(): boolean {
		return this.hasAttribute('data-disable-dragging') && this.getAttribute('data-disable-dragging') === 'true'
	}
	set disableDragging(val: boolean) {
		this.setAttribute('data-disable-dragging', val ? 'true' : 'false')
	}

	connectedCallback(): void {
		this.slidesCollectionEl = this.querySelector('.slides-collection')
		this.controlsContainerEl = this.querySelector('.carousel-controls')
		this.slideEls = this.slidesCollectionEl.querySelectorAll('.slide')
		this.rtlModifier = this._isRTL() ? -1 : 1

		/* Go ahead and number all the slides if not already set */
		if (!this.slideEls[0].getAttribute('data-slide-num')) {
			this.slideEls.forEach((el: HTMLElement, idx: number) => {
				el.setAttribute('data-slide-num', (idx + 1).toString())
			})
		}

		this.activateSlide(0)

		if (this.transitionOverride === 'fade') {
			// Override default transition to fade
			this._positionSlides()
		} else {
			// Default transition is side-scroll

			/* Add Offset and Peek */
			if (this.offsetWithPeek) {
				if (!this.querySelector('.left-overflow-col')) {
					const leftOverflowEl = document.createElement('div')
					leftOverflowEl.classList.add('left-overflow-col')
					this.slidesCollectionEl.prepend(leftOverflowEl)
				}

				if (!this.querySelector('.right-overflow-col')) {
					const rightOverflowEl = document.createElement('div')
					rightOverflowEl.classList.add('right-overflow-col')
					this.slidesCollectionEl.appendChild(rightOverflowEl)
				}
			}

			if (!this.disableDragging) {
				this._setupDragging()
			}
		}

		this.resizeObserver = new ResizeObserver(() => {
			if (this.transitionOverride === 'fade') {
				this._positionSlides()
			} else if (!this.loop) {
				if (this.slidesCollectionEl.style.transform) {
					const currTransform = this.slidesCollectionEl.style.transform
					const transformMatrix = new window.WebKitCSSMatrix(currTransform === 'none' ? '' : currTransform)

					if (transformMatrix && transformMatrix.m41) {
						this.slidesCollectionEl.style.transitionDuration = '0ms'
						this.slidesCollectionEl.style.transform = `translate3d(${-1 * this.rtlModifier * this.slideEls[this.activeSlideNum].clientWidth * this.activeSlideNum}px, 0px, 0px)`
					}
				}
			}
		})

		this.resizeObserver.observe(this.slidesCollectionEl)

		if (this.controlsContainerEl) {
			this.controlsContainerEl.addEventListener('click', this.boundControlsClickHandler)
		}
	}

	disconnectedCallback(): void {
		if (this.controlsContainerEl) {
			this.controlsContainerEl.removeEventListener('click', this.boundControlsClickHandler)
		}

		this.slidesCollectionEl.removeEventListener('mousedown', this.boundDragStartHandler)
		this.slidesCollectionEl.removeEventListener('touchstart', this.boundDragStartHandler)

		this.slidesCollectionEl.removeEventListener('mousemove', this.boundDragMoveHandler)
		this.slidesCollectionEl.removeEventListener('touchmove', this.boundDragMoveHandler)

		this.slidesCollectionEl.removeEventListener('mouseup', this.boundDragEndHandler)
		this.slidesCollectionEl.removeEventListener('touchend', this.boundDragEndHandler)

		this.removeEventListener('mouseleave', this.boundMouseLeaveHandler)

		this.slidesCollectionEl.addEventListener('click', this.boundClickHandler.bind(this))

		if (this.resizeObserver) {
			this.resizeObserver.unobserve(this)
		}
	}

	private _isRTL(): boolean {
		return globalThis.getComputedStyle(this).direction === 'rtl'
	}

	private _positionSlides(): void {
		this.slideEls.forEach((s: HTMLElement, idx: number) => {
			s.style.transform = `translate3d(${-1 * this.rtlModifier * this.slidesCollectionEl.offsetWidth * idx}px,0,0)`
		})
	}

	/**
	 * Method to handle the dot collection click events.  If determined a dot/meatball indicator was clicked
	 * it will call the activateSlide() method to change the active slide to the num passed in.
	 *
	 * @param e {Event} Event data from the incoming click event
	 * @private
	 */
	private _handleControlsClick(e: Event): void {
		const target = e.target as HTMLElement

		if (target.classList.contains('gmx-prev-ctrl')) {
			this.activatePreviousSlide()
			this.dispatchEvent(new CustomEvent('gmxCarouselPreviousControlClicked'))
		} else if (target.classList.contains('gmx-next-ctrl')) {
			this.activateNextSlide()
			this.dispatchEvent(new CustomEvent('gmxCarouselNextControlClicked'))
		}
	}

	activateSlide(slideNum: number): void {
		this.slideEls.forEach((slide: HTMLElement, idx: number) => {
			slide.classList.toggle('active', idx === slideNum)
		})
	}

	/**
	 * Jump the carousel directly to an arbitrary slide index without animating through the
	 * intermediate slides and without reordering any slides. Used to preset the initial in-view
	 * slide (e.g. from a model/trim entry parameter).
	 *
	 * Not supported for side-scroll loop mode: loop mode reorders the DOM and assumes the active
	 * slide stays at index 0, so a non-reordering jump would corrupt that state. In that case this
	 * is a no-op (the carousel keeps its default initial slide).
	 *
	 * @param slideNum {number} Zero-based index of the slide to make active
	 */
	goToSlide(slideNum: number): void {
		if (slideNum < 0 || slideNum >= this.slideEls.length || slideNum === this.activeSlideNum) {
			return
		}

		if (this.loop && this.transitionOverride !== 'fade') {
			return
		}

		this.activeSlideNum = slideNum
		this.activateSlide(slideNum)

		if (this.transitionOverride !== 'fade') {
			// Side-scroll transition positions the collection via a translate transform.
			this.slidesCollectionEl.style.transitionDuration = '0ms'
			this.slidesCollectionEl.style.transform = `translate3d(${-1 * this.rtlModifier * this.slideEls[slideNum].clientWidth * slideNum}px, 0px, 0px)`
			// Restore default transition duration for subsequent user-driven moves.
			requestAnimationFrame(() => {
				this.slidesCollectionEl.style.transitionDuration = ''
			})
		}

		// Match the other transitions: report the slide's stable data-slide-num (via the -1 offset
		// that _dispatchSlideChangeEvent re-adds) so detail.slideNum stays correct regardless of order.
		this._dispatchSlideChangeEvent(
			parseInt(this.slideEls[slideNum].getAttribute('data-slide-num'), 10) - 1,
			this.slideEls[slideNum].classList.contains('text-invert')
		)
	}

	/**
	 * Make the previous slide the new 'active' slide, ie, first in list
	 */
	activatePreviousSlide(): void {
		let newActiveSlide: HTMLElement
		let needsEventDispatch = false

		if (this.transitionOverride === 'fade') {
			this.activeSlideNum =
				this.activeSlideNum === 0 ? (this.activeSlideNum = this.slideEls.length - 1) : this.activeSlideNum - 1
			this.activateSlide(this.activeSlideNum)

			newActiveSlide = this.slideEls[this.activeSlideNum]
			needsEventDispatch = true
		} else {
			if (this.loop) {
				newActiveSlide = this.slideEls[this.slideEls.length - 1]
				needsEventDispatch = true

				const lastSlide = this.slideEls[this.slideEls.length - 1]
				this.slidesCollectionEl.removeChild(lastSlide)
				this.slidesCollectionEl.prepend(lastSlide)
				this.slidesCollectionEl.style.transitionDuration = '0ms'
				this.slidesCollectionEl.style.transform = `translate3d(${-1 * this.rtlModifier * lastSlide.clientWidth}px, 0px, 0px)`

				setTimeout(() => {
					this.slidesCollectionEl.style.transitionDuration = ''
					this.slidesCollectionEl.style.transform = `translate3d(0px, 0px, 0px)`
					this.slideEls = this.slidesCollectionEl.querySelectorAll('.slide')
					this.activateSlide(0)
				}, 0)
			} else if (this.activeSlideNum > 0) {
				this.activeSlideNum -= 1
				this.slidesCollectionEl.style.transitionDuration = ''
				this.slidesCollectionEl.style.transform = `translate3d(${-1 * this.rtlModifier * this.slideEls[this.activeSlideNum].clientWidth * this.activeSlideNum}px, 0px, 0px)`
				newActiveSlide = this.slideEls[this.activeSlideNum]
				this.activateSlide(this.activeSlideNum)
				needsEventDispatch = true
			}
		}

		if (needsEventDispatch) {
			this._dispatchSlideChangeEvent(
				parseInt(newActiveSlide.getAttribute('data-slide-num'), 10) - 1,
				newActiveSlide.classList.contains('text-invert')
			)
		}
	}

	/**
	 * Make the next slide the new 'active' slide, ie, first in list
	 */
	activateNextSlide(): void {
		let newActiveSlide: HTMLElement
		let needsEventDispatch = false

		if (this.transitionOverride === 'fade') {
			this.activeSlideNum = this.activeSlideNum === this.slideEls.length - 1 ? 0 : this.activeSlideNum + 1
			this.activateSlide(this.activeSlideNum)

			newActiveSlide = this.slideEls[this.activeSlideNum]
			needsEventDispatch = true
		} else {
			if (this.loop) {
				newActiveSlide = this.slideEls[1]
				needsEventDispatch = true

				const firstSlide = this.slideEls[0]
				this.slidesCollectionEl.addEventListener(
					'transitionend',
					() => {
						this.slidesCollectionEl.style.transitionDuration = '0ms'
						this.slidesCollectionEl.style.transform = `translate3d(0px, 0px, 0px)`
						this.slidesCollectionEl.removeChild(firstSlide)
						this.slidesCollectionEl.appendChild(firstSlide)

						this.slideEls = this.slidesCollectionEl.querySelectorAll('.slide')
						this.activateSlide(0)
					},
					{ once: true }
				)

				this.slidesCollectionEl.style.transitionDuration = ''
				this.slidesCollectionEl.style.transform = `translate3d(${-1 * this.rtlModifier * firstSlide.clientWidth}px, 0px, 0px)`
			} else if (this.activeSlideNum !== this.slideEls.length - 1) {
				this.activeSlideNum += 1
				this.slidesCollectionEl.style.transitionDuration = ''
				this.slidesCollectionEl.style.transform = `translate3d(${-1 * this.rtlModifier * this.slideEls[this.activeSlideNum].clientWidth * this.activeSlideNum}px, 0px, 0px)`

				newActiveSlide = this.slideEls[this.activeSlideNum]
				needsEventDispatch = true

				this.activateSlide(this.activeSlideNum)
			}
		}

		if (needsEventDispatch) {
			this._dispatchSlideChangeEvent(
				parseInt(newActiveSlide.getAttribute('data-slide-num'), 10) - 1,
				newActiveSlide.classList.contains('text-invert')
			)
		}
	}

	/**
	 * Method that will setup the swipe/drag behavior
	 * @private
	 */
	private _setupDragging(): void {
		this.slidesCollectionEl.addEventListener('mousedown', this.boundDragStartHandler)
		this.slidesCollectionEl.addEventListener('touchstart', this.boundDragStartHandler, { passive: true })

		this.slidesCollectionEl.addEventListener('mousemove', this.boundDragMoveHandler)
		this.slidesCollectionEl.addEventListener('touchmove', this.boundDragMoveHandler, { passive: true })

		/* this.slidesCollectionEl.addEventListener('wheel', (event) => {
			if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
				console.log(event.deltaX) // > 0 right, < 0 left
			}
		}) */

		this.slidesCollectionEl.addEventListener('mouseup', this.boundDragEndHandler)
		this.slidesCollectionEl.addEventListener('touchend', this.boundDragEndHandler, { passive: true })

		this.addEventListener('mouseleave', this.boundMouseLeaveHandler)

		this.slidesCollectionEl.addEventListener('click', this.boundClickHandler.bind(this))
	}

	/**
	 * Callback for when the component gets a mousedown or touchstart event.
	 * @param e
	 * @private
	 */
	private _handleDragStart(e: MouseEvent | TouchEvent): void {
		this.movedBeyondThreshold = false

		if (!this.slidesInTransition) {
			this.xDiff = 0

			this.slidesCollectionEl.classList.add('grabbed')
			this.isGrabbed = true

			if ('touches' in e && e.type === 'touchstart') {
				this.grabbedX = e.touches[0].clientX
			} else {
				if ('clientX' in e) {
					this.grabbedX = e.clientX
				}
			}

			// Override transition while drag/swipe in progress
			this.slidesCollectionEl.style.transitionDuration = '0s'
		}
	}

	/**
	 * Callback for when the component gets a mousemove event.
	 * @param e
	 * @private
	 */
	private _handleDragMove(e: MouseEvent | TouchEvent): void {
		if (this.isGrabbed && !this.slidesInTransition) {
			let currX
			const currentTime = Date.now()
			const deltaTime = currentTime - this.prevTime

			if ('touches' in e && e.type === 'touchmove') {
				currX = e.touches[0].clientX
			} else {
				if ('clientX' in e) {
					currX = e.clientX
				}
			}

			this.xDiff = (currX - this.grabbedX) * this.rtlModifier
			this.prevTime = currentTime

			if (deltaTime > 0) {
				const distance = Math.sqrt(this.xDiff * this.xDiff)
				this.mouseVelocity = distance / deltaTime // Velocity in pixels per millisecond
			}

			if (Math.abs(this.xDiff) > GMXCarousel.DRAG_THRESHOLD) {
				this.movedBeyondThreshold = true
			}

			if (this.xDiff > 0) {
				// Go back
				if (this.loop) {
					if (!this.lastSlide) {
						this.lastSlide = this.slideEls[this.slideEls.length - 1] as HTMLElement
						this.slidesCollectionEl.removeChild(this.lastSlide)
						this.slidesCollectionEl.prepend(this.lastSlide)
					}
					this.slidesCollectionEl.style.transform = `translate3d(${this.rtlModifier * this.xDiff - this.rtlModifier * this.lastSlide.clientWidth}px, 0px, 0px)`
				} else {
					if (this.activeSlideNum > 0) {
						this.slidesCollectionEl.style.transform = `translate3d(${this.rtlModifier * this.xDiff - this.rtlModifier * this.slideEls[this.activeSlideNum].clientWidth * this.activeSlideNum}px, 0px, 0px)`
					} else {
						this.xDiff = 0
						this.slidesCollectionEl.style.transform = `translate3d(0px, 0px, 0px)`
					}
				}
			} else if (this.xDiff < 0) {
				// go forward
				if (this.loop) {
					if (this.lastSlide) {
						this.slidesCollectionEl.removeChild(this.lastSlide)
						this.slidesCollectionEl.appendChild(this.lastSlide)
						this.lastSlide = null
					}
					this.slidesCollectionEl.style.transform = `translate3d(${this.rtlModifier * this.xDiff}px, 0px, 0px)`
				} else {
					if (this.activeSlideNum !== this.slideEls.length - 1) {
						this.slidesCollectionEl.style.transform = `translate3d(${this.rtlModifier * this.xDiff - this.rtlModifier * this.slideEls[this.activeSlideNum].clientWidth * this.activeSlideNum}px, 0px, 0px)`
					} else {
						this.xDiff = 0
						this.slidesCollectionEl.style.transform = `translate3d(${this.rtlModifier * this.xDiff - this.rtlModifier * this.slideEls[this.activeSlideNum].clientWidth * this.activeSlideNum}px, 0px, 0px)`
					}
				}
			}

			// Finally, dispatch an event that the user is dragging/swiping the carousel
			this.dispatchEvent(
				new CustomEvent('gmxCarouselDragging', {
					detail: {
						xDiff: this.xDiff,
						mouseVelocity: this.mouseVelocity,
					},
				})
			)
		}
	}

	/**
	 * Callback for when the components gets a mouseup or touchend event.
	 * @param e
	 * @private
	 */
	private _handleDragEnd(): void {
		const now = Date.now()
		const deltaTime = now - this.prevTime

		if (!this.slidesInTransition) {
			const slideWidth = this.lastSlide
				? this.lastSlide.clientWidth
				: this.slidesCollectionEl.querySelector('.slide').clientWidth

			if (deltaTime > 0) {
				const distance = Math.sqrt(this.xDiff * this.xDiff)
				this.mouseVelocity = distance / deltaTime // Velocity in pixels per millisecond
			} else {
				this.mouseVelocity = 0
			}

			// If the user has dragged the slider more than 50% of the slide width, will need to transition to the next (or previous)
			// Else slide back into original position
			// NOTE:  The mouseVelocity is a guess based on my testing.  Might need to get tweaked later on
			if (Math.abs(this.xDiff) >= slideWidth / 4 || this.mouseVelocity > 3) {
				this._disableControlsContainer()
				this.slidesInTransition = true

				if (this.loop) {
					this.slidesCollectionEl.addEventListener(
						'transitionend',
						() => {
							if (this.xDiff < 0) {
								this.slidesCollectionEl.style.transitionDuration = '0ms'
								this.slidesCollectionEl.style.transform = `translate3d(0px, 0px, 0px)`

								// Since we moved forward and reset the slider position, can remove the first slide so that
								// the first slide is now the next slide
								const firstSlide = this.slidesCollectionEl.querySelector('.slide')
								this.slidesCollectionEl.removeChild(firstSlide)
								this.slidesCollectionEl.appendChild(firstSlide)

								setTimeout(() => {
									this.slidesCollectionEl.style.transitionDuration = ''
								}, 0)
							}

							this.xDiff = 0
							this.lastSlide = null
							this._enableControlsContainer()
							this.slidesInTransition = false
							this.slideEls = this.slidesCollectionEl.querySelectorAll('.slide')

							this.activateSlide(0)
							this._dispatchSlideChangeEvent(
								0,
								this.slideEls[this.activeSlideNum].classList.contains('text-invert')
							)
						},
						{ once: true }
					)

					this.slidesCollectionEl.style.transitionDuration = ''
					if (this.xDiff > 0) {
						// Slide backwards
						this.slidesCollectionEl.style.transform = `translate3d(0px, 0px, 0px)`
					} else {
						// Slide forwards
						this.slidesCollectionEl.style.transform = `translate3d(${-1 * this.rtlModifier * slideWidth}px, 0px, 0px)`
					}
				} else {
					// Not loop
					this.slidesCollectionEl.addEventListener(
						'transitionend',
						() => {
							this.slidesCollectionEl.style.transitionDuration = ''
							this.slidesCollectionEl.style.transitionTimingFunction = ''
							this.xDiff = 0
							this.lastSlide = null
							this._enableControlsContainer()
							this.slidesInTransition = false
						},
						{ once: true }
					)

					if (this.xDiff > 0) {
						// Slide backwards
						this.activeSlideNum -= 1
					} else {
						// Slide forwards
						this.activeSlideNum += 1
					}

					this.activateSlide(this.activeSlideNum)

					this._dispatchSlideChangeEvent(
						this.activeSlideNum,
						this.slideEls[this.activeSlideNum].classList.contains('text-invert')
					)

					this.slidesCollectionEl.style.transitionDuration = '300ms'
					this.slidesCollectionEl.style.transitionTimingFunction = 'ease'
					this.slidesCollectionEl.style.transform = `translate3d(${this.rtlModifier * this.slideEls[this.activeSlideNum].clientWidth * this.activeSlideNum * -1}px, 0px, 0px)`
				}
			} else {
				// If we are here, the user didn't drag far enough to transition, so reset position
				this.slidesCollectionEl.style.transitionDuration = ''
				this._disableControlsContainer()
				this.slidesInTransition = true

				if (this.loop) {
					const lastSlide = this.lastSlide

					this.slidesCollectionEl.addEventListener(
						'transitionend',
						() => {
							if (this.xDiff > 0) {
								if (lastSlide) {
									this.slidesCollectionEl.style.transitionDuration = '0ms'
									this.slidesCollectionEl.removeChild(lastSlide)
									this.slidesCollectionEl.appendChild(lastSlide)
								}
								this.lastSlide = null
								this.slidesCollectionEl.style.transform = `translate3d(0px, 0px, 0px)`
								this.xDiff = 0
							}

							this._enableControlsContainer()
							this.slidesInTransition = false
						},
						{ once: true }
					)

					if (this.xDiff > 0) {
						this.slidesCollectionEl.style.transform = `translate3d(${this.rtlModifier * lastSlide.clientWidth * -1}px, 0px, 0px)`
					} else if (this.xDiff < 0) {
						// Had slid forwards, go back to original position
						this.slidesCollectionEl.style.transform = `translate3d(0px, 0px, 0px)`
					} else {
						this._enableControlsContainer()
						this.slidesInTransition = false
						this.xDiff = 0
					}
				} else {
					const currentSlide = this.slideEls[this.activeSlideNum]

					this.slidesCollectionEl.addEventListener(
						'transitionend',
						() => {
							this.xDiff = 0
							this._enableControlsContainer()
							this.slidesInTransition = false
						},
						{ once: true }
					)

					if (this.xDiff !== 0) {
						this.slidesCollectionEl.style.transform = `translate3d(${this.rtlModifier * currentSlide.clientWidth * this.activeSlideNum * -1}px, 0px, 0px)`
					} else {
						this._enableControlsContainer()
						this.slidesInTransition = false
						this.xDiff = 0
					}
				}
			}
		}

		this.grabbedX = null
		this.isGrabbed = false

		this.slidesCollectionEl.classList.remove('grabbed')
	}

	/**
	 * Disable the controls container so it cannot be interacted with.  This is generally done only when a transition
	 * in slides is taking place.
	 * @private
	 */
	private _disableControlsContainer(): void {
		if (this.controlsContainerEl) {
			this.controlsContainerEl.classList.add('enabled')
		}
	}

	/**
	 * Enable the controls container so that it can be interacted with.  Generally done after a slide transition has
	 * occurred.
	 * @private
	 */
	private _enableControlsContainer(): void {
		if (this.controlsContainerEl) {
			this.controlsContainerEl.classList.remove('enabled')
		}
	}

	private _handleMouseLeave(): void {
		if (this.isGrabbed) {
			this._handleDragEnd()
		}
	}

	/**
	 * Method to dispatch a slide change event when the active slide changes
	 *
	 * @param slideNum {number} The new active slide number
	 * @param isTextInvert {boolean} Flag indicating if the text should be inverted for this slide
	 * @private
	 */
	private _dispatchSlideChangeEvent(slideNum: number, isTextInvert: boolean): void {
		this.dispatchEvent(
			new CustomEvent('gmxCarouselSlideChange', {
				detail: {
					slideNum: slideNum + 1, // Need to +1 b/c slideNum is zero based index
					isTextInvert: isTextInvert,
				},
			})
		)
	}

	private _handleClick(e: MouseEvent): void {
		if (this.movedBeyondThreshold) {
			e.preventDefault()
			e.stopImmediatePropagation()
		}

		this.movedBeyondThreshold = false
	}
}

if (!customElements.get('gmx-carousel')) {
	customElements.define('gmx-carousel', GMXCarousel)
}
